import { useEffect, useMemo, useState } from 'react';
import {
    mdiSchool,
    mdiCalendarMonthOutline,
    mdiAccountMultiplePlusOutline,
    mdiAccountEdit,
    mdiCalendarEdit,
    mdiBankOutline,
    mdiTicketConfirmationOutline,
    mdiFileDocumentOutline,
} from '@mdi/js';
import { useAuth } from '../contexts/AuthContext';
import webApiClient from '../api/client';

/**
 * useOnboardingChecklist — the single source of truth for the "Complete your setup"
 * checklist: which steps this org sees, whether each is done, and the counts that
 * drive the dashboard badge and the progress bar.
 *
 * It lives in a hook (rather than inside OnboardingChecklistModal) because two
 * surfaces need the same answer: the dashboard renders an unresolved-count badge on
 * the checklist icon, and the modal renders the list itself. Both read one hook call
 * from Dashboard, so the status endpoint is fetched once per dashboard load.
 *
 * Note this is a deliberate change from the original lazy fetch-on-modal-open: a
 * badge has to know the count before the modal is opened. The endpoint is a handful
 * of indexed existence checks, so it is cheap enough to run on dashboard load.
 *
 * ── Three states per step, not two ───────────────────────────────────────────────
 *   'complete'   green check  — the backend confirmed the org did this.
 *   'prefilled'  grey check   — ships with working standard terms, so there is no
 *                               done/not-done. Already working, NOT outstanding.
 *   'incomplete' amber check  — genuinely still to do.
 *
 * Only 'incomplete' steps get the amber marker and drive the dashboard badge count
 * (the badge itself is a red disc, the standard notification-count treatment).
 * Folding the grey pre-filled cards in would overstate what is actually outstanding.
 * The progress bar counts them as done for the same reason, which keeps the bar and
 * the badge arithmetically consistent: complete + incomplete === total.
 *
 * @returns {{
 *   groups: Array,          gated + filtered groups, each step carrying its `state`
 *   hasStatus: boolean,     the status fetch resolved (false => render no states)
 *   totalCount: number,
 *   completeCount: number,  green + grey
 *   incompleteCount: number, amber steps; this is the badge number
 * }}
 */
export function useOnboardingChecklist() {
    const { user, organisation, checkFeature } = useAuth();

    // Per-step completion flags keyed by step.key. null until the fetch resolves, and
    // stays null if it fails, in which case the checklist degrades to plain nav links
    // with no states, no bar and no badge.
    const [setupStatus, setSetupStatus] = useState(null);

    // Keyed off the JWT's active org (the same id the endpoint reads server-side from
    // req.auth.organisationId) so the fetch does not wait on the org profile load, and
    // re-runs cleanly on an org switch.
    const organisationId = user?.organisationId;

    useEffect(() => {
        if (!organisationId) return;
        let cancelled = false;
        (async () => {
            try {
                const response = await webApiClient.get('/api/v2/metrics/setup-status');
                if (!cancelled && response.ok) {
                    setSetupStatus(response.data.setupStatus || {});
                }
            } catch {
                // Non-fatal: the checklist still works as plain nav links.
            }
        })();
        return () => { cancelled = true; };
    }, [organisationId]);

    const isDanceSchool = !!organisation?.isDanceSchool;
    const isEventOrganiser = !!organisation?.isEventOrganiser;
    const hasClasses = checkFeature('classManagement');
    const hasPrivateLessons = checkFeature('privateLessons');
    const hasTicketing = checkFeature('ticketing');

    return useMemo(() => {
        // Until the fetch resolves we know nothing about any step, so every step's state
        // is null and all counts are 0. Classifying early would default the whole list to
        // "incomplete" and flash a full-count red badge for the life of the request.
        const hasStatus = setupStatus !== null;

        // Steps are grouped by setup area, and each step is gated by the tool/account
        // type it belongs to, so the list only ever shows tasks relevant to how this org
        // said it would use the platform. Empty groups (and steps) are filtered out.
        const groups = [
            {
                key: 'school',
                heading: isDanceSchool ? 'Your dance school' : 'Your account',
                steps: [
                    {
                        key: 'school-details',
                        // Schools and event organisers both need this — an organiser's logo
                        // and location are rendered on every event landing page. Dance
                        // Artists are excluded: they present through their instructor
                        // profile, which has its own photo, cover and location.
                        show: isDanceSchool || isEventOrganiser,
                        icon: mdiSchool,
                        title: isDanceSchool
                            ? 'Set up your dance school details'
                            : 'Set up your account details',
                        description: isDanceSchool
                            ? 'Add your school name, logo, location and dance styles.'
                            : 'Set your location, and add a logo if you have one.',
                        route: '/settings',
                    },
                    {
                        key: 'class-management',
                        show: hasClasses,
                        icon: mdiCalendarMonthOutline,
                        title: 'Set up class management',
                        description: 'Create your terms, classes and start taking enrolments.',
                        route: '/classes/manage',
                    },
                    {
                        key: 'import-students',
                        show: hasClasses,
                        icon: mdiAccountMultiplePlusOutline,
                        title: 'Upload your existing students',
                        description: 'Bring across your current students with a spreadsheet import.',
                        route: '/classes/students/import',
                    },
                    {
                        key: 'class-terms',
                        // Gated on the Class Management tool, not on isDanceSchool: these are
                        // the terms students agree to when ENROLLING, so with classes off there
                        // are no enrolments and nothing to set terms for. (isDanceSchool is
                        // registration-seed metadata and gates nothing at runtime.)
                        show: hasClasses,
                        icon: mdiFileDocumentOutline,
                        title: 'Set your class enrolment terms and conditions',
                        description: 'Set the cancellation, refund and other terms students agree to when enrolling.',
                        route: '/settings',
                        // These come pre-filled with working standard terms, so there is no
                        // "done / not done" — shown with a neutral grey check, not a green one.
                        prefilled: true,
                    },
                ],
            },
            {
                key: 'private-lessons',
                heading: 'Private lessons',
                steps: [
                    {
                        key: 'booking-profile',
                        show: hasPrivateLessons,
                        icon: mdiAccountEdit,
                        title: 'Set up your booking profile',
                        description: 'Add your photo, bio and rate so students can book you.',
                        route: '/instructor-profile-setup/profile',
                    },
                    {
                        key: 'availability',
                        show: hasPrivateLessons,
                        icon: mdiCalendarEdit,
                        title: 'Set your availability',
                        description: 'Choose the days and times you can take private lessons.',
                        route: '/manage-availability',
                    },
                    {
                        key: 'private-terms',
                        show: hasPrivateLessons,
                        icon: mdiFileDocumentOutline,
                        title: 'Set your terms and conditions',
                        description: 'Set the cancellation, refund and other terms students agree to when booking.',
                        route: '/instructor-profile-setup/profile',
                        // Pre-filled with working standard terms — grey check, not green.
                        prefilled: true,
                    },
                ],
            },
            {
                key: 'get-paid',
                heading: 'Get paid',
                steps: [
                    {
                        key: 'bank-account',
                        // Banking is universal — every domain (classes, private lessons,
                        // ticketing) settles money to the connected bank account — so this
                        // shows for any org, in its own group rather than under tickets.
                        show: hasClasses || hasPrivateLessons || hasTicketing || isDanceSchool,
                        icon: mdiBankOutline,
                        title: 'Set up your bank account to accept payments',
                        description: 'Connect your bank in Manage Finances so you can get paid.',
                        route: '/manage-finances',
                    },
                ],
            },
            {
                key: 'tickets',
                heading: 'Event tickets',
                steps: [
                    {
                        key: 'ticket-settings',
                        show: hasTicketing,
                        icon: mdiTicketConfirmationOutline,
                        title: 'Set up your ticket settings',
                        description: 'Configure how you sell event tickets and check guests in.',
                        route: '/ticketing/settings',
                    },
                ],
            },
        ]
            .map(group => ({
                ...group,
                steps: group.steps
                    .filter(step => step.show)
                    // Only some steps have a completion signal (see the backend
                    // setupStatus.service.js). A pre-filled step is never "complete"
                    // because it has no flag — it is its own state. State stays null
                    // until the status is actually known.
                    .map(step => ({
                        ...step,
                        state: !hasStatus
                            ? null
                            : setupStatus[step.key] === true
                                ? 'complete'
                                : step.prefilled ? 'prefilled' : 'incomplete',
                    })),
            }))
            .filter(group => group.steps.length > 0);

        const allSteps = groups.flatMap(group => group.steps);
        const incompleteCount = allSteps.filter(step => step.state === 'incomplete').length;

        return {
            groups,
            hasStatus,
            totalCount: allSteps.length,
            // Both counts stay 0 while the status is unknown, so a consumer that renders
            // straight off a count (the dashboard badge) shows nothing rather than a
            // wrong number.
            completeCount: hasStatus ? allSteps.length - incompleteCount : 0,
            incompleteCount,
        };
    }, [setupStatus, isDanceSchool, isEventOrganiser, hasClasses, hasPrivateLessons, hasTicketing]);
}

export default useOnboardingChecklist;
